import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, ArrowRight, CheckCircle, Leaf, Package, BadgeCheck } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import SEO from '../components/SEO';
import { productCategories } from '../mockData';
import { useLanguage } from '../i18n/LanguageContext';

// SEO keywords per product category
const SEO_KEYWORDS = {
  "paper-chemicals": "AKD, alkyl ketene dimer, surface sizing, dry strength resin, DSR, defoamer, retention aid, drainage aid, flocculant, coagulant, anionic trash control, ATC, AS ATC 1300, cationic demand reduction, AKD booster, ply bond improvement, stiffness improvement, ash retention, native starch cationization, burst factor booster, paper chemicals supplier, paper mill chemicals, eco-friendly paper chemicals, VB Global Coimbatore",
  "construction-chemicals": "concrete admixture, flyash brick admixture, paver block admixture, interlocking bricks admixture, tile adhesives, grouts, waterproofing chemicals, construction chemicals supplier, concrete additives, building chemicals India, VB Global Coimbatore",
  "ss-wire-mesh": "SS wire mesh, stainless steel wire mesh, open ended wire mesh, diagonal seam wire mesh, woven wire mesh, industrial wire mesh, 5 to 200 mesh, paper machine wire, filtration mesh, Maheen Wire Mesh Industries authorised dealer, Maheen wire mesh Jaipur, VB Global Coimbatore",
  "high-pressure-showers": "high pressure showers, paper machine showers, oscillating showers, stationary showers, brush showers, tube in tube showers, starch spray showers, high pressure nozzles, low pressure nozzles, edge cutting nozzles, trim squirt nozzles, duplex filters, paper machine cleaning, industrial spray nozzles, Audley Industries authorised dealer, Audley showers Calcutta, VB Global",
  "dyes": "paper dyes, direct dyes, basic dyes, acid dyes, OBA, optical brightening agents, optical brighteners, fluorescent whitening agent, paper coloring dyes, kraft paper dyes, tissue paper dyes, paper shading dyes, cationic dyes, anionic dyes, paper dyes supplier India, dyes supplier Coimbatore, VB Global"
};

const ProductDetail = () => {
  const { slug } = useParams();
  const { t } = useLanguage();
  const product = productCategories.find(p => p.slug === slug);

  if (!product) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">{t('product.notFound')}</h2>
          <Link to="/">
            <Button className="bg-[#8B2C3A] hover:bg-[#6B1C2A] text-white">{t('product.goHome')}</Button>
          </Link>
        </div>
      </div>
    );
  }

  const name = t(`categories.${slug}.name`);

  return (
    <div className="min-h-screen bg-gray-50">
      <SEO 
        title={product.name}
        description={`${product.description}. Browse our complete range of ${product.name.toLowerCase()} from VB Global - your trusted supplier in Coimbatore, India.`}
        keywords={SEO_KEYWORDS[slug] || ""}
      />
      {/* Breadcrumb */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <Link to="/" className="inline-flex items-center text-[#8B2C3A] hover:text-[#6B1C2A] transition-colors">
            <ArrowLeft className="w-4 h-4 mr-2" />
            {t('product.backHome')}
          </Link>
        </div>
      </div>

      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-br from-[#8B2C3A] to-[#6B1C2A] text-white">
        <div className="absolute inset-0 bg-black/10"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 bg-emerald-500/20 text-emerald-100 px-4 py-2 rounded-full mb-6 backdrop-blur-sm border border-emerald-400/30">
              <Leaf className="w-4 h-4" />
              <span className="text-sm font-medium">{t('product.ecoBadge')}</span>
            </div>
            <h1 data-testid="product-page-title" className="text-4xl md:text-5xl font-bold mb-4">{name}</h1>
            <p className="text-xl text-gray-200">{t(`categories.${slug}.description`)}</p>
            {product.dealer && (
              <div data-testid="product-dealer-badge" className="mt-6 inline-flex items-center gap-2 bg-[#D4AF37] text-gray-900 px-4 py-2 rounded-full font-semibold shadow-md">
                <BadgeCheck className="w-5 h-5" />
                <span>{t('product.authorisedDealer')}: {product.dealer}</span>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Products Grid */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">{t('product.rangeTitle', { name })}</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {product.products.map((item, index) => (
              <Card key={index} data-testid={`product-card-${index}`} className="border-2 hover:border-[#8B2C3A] transition-all hover:shadow-lg">
                <CardHeader>
                  <div className="w-12 h-12 bg-[#8B2C3A] rounded-lg flex items-center justify-center mb-4">
                    <Package className="w-6 h-6 text-white" />
                  </div>
                  <CardTitle className="text-xl">{item.name}</CardTitle>
                  <CardDescription className="text-gray-600">{item.description}</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    <h4 className="font-semibold text-gray-900 mb-2">{t('product.keyBenefits')}</h4>
                    {item.benefits.map((benefit, idx) => (
                      <div key={idx} className="flex items-start gap-2">
                        <CheckCircle className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                        <span className="text-sm text-gray-700">{benefit}</span>
                      </div>
                    ))}
                  </div>
                  {item.link && (
                    <Link to={item.link} data-testid={`product-card-link-${index}`} className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-[#8B2C3A] hover:text-[#6B1C2A]">
                      {t('product.readArticle')} <ArrowRight className="w-4 h-4" />
                    </Link>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">
                {t('product.whyChoose', { name })}
              </h2>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <div className="bg-emerald-100 p-2 rounded-lg">
                    <Leaf className="w-5 h-5 text-emerald-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">{t('product.ecoFormulations')}</h3>
                    <p className="text-gray-600">{t('product.ecoFormulationsDesc')}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="bg-[#8B2C3A]/10 p-2 rounded-lg">
                    <CheckCircle className="w-5 h-5 text-[#8B2C3A]" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">{t('product.qualityAssured')}</h3>
                    <p className="text-gray-600">{t('product.qualityAssuredDesc')}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="bg-[#D4AF37]/10 p-2 rounded-lg">
                    <Package className="w-5 h-5 text-[#D4AF37]" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">{t('product.reliableSupply')}</h3>
                    <p className="text-gray-600">{t('product.reliableSupplyDesc')}</p>
                  </div>
                </div>
                {product.dealer && (
                  <div className="flex items-start gap-3" data-testid="product-dealer-item">
                    <div className="bg-[#D4AF37]/10 p-2 rounded-lg">
                      <BadgeCheck className="w-5 h-5 text-[#D4AF37]" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-1">{t('product.authorisedDealer')}</h3>
                      <p className="text-gray-600">{t('product.authorisedDealerDesc', { dealer: product.dealer })}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div className="rounded-2xl overflow-hidden shadow-xl">
              <img 
                src={product.image} 
                alt={product.name}
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-[#8B2C3A] to-[#6B1C2A] text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-4">
            {t('product.interested', { name })}
          </h2>
          <p className="text-xl mb-8 text-gray-200">
            {t('product.ctaSub')}
          </p>
          <Link to="/contact">
            <Button size="lg" className="bg-[#D4AF37] hover:bg-[#B8941F] text-gray-900 font-semibold">
              {t('product.requestQuote')}
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default ProductDetail;
